// Unified notification-sound synthesis using Web Audio API.
// No external assets. Used by driver alerts, in-app toast center, and profile preview.
export const LOCAL_KEY = "talabya_notif_sound_v1";

let sharedCtxRef = { current: null };
function ensureCtx(external) {
  const ref = external || sharedCtxRef;
  if (ref.current) return ref.current;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ref.current = new AC();
    return ref.current;
  } catch { return null; }
}

const PATTERNS = {
  // Warm two-tone chime — Talabya default
  classic: [
    { f: 880,  t: 0.0,  d: 0.18, gain: 0.30 },
    { f: 1320, t: 0.20, d: 0.22, gain: 0.28 },
    { f: 880,  t: 0.44, d: 0.18, gain: 0.28 },
  ],
  // Three ascending crystal notes
  chime: [
    { f: 660,  t: 0.0,  d: 0.14, gain: 0.28 },
    { f: 990,  t: 0.16, d: 0.14, gain: 0.28 },
    { f: 1480, t: 0.32, d: 0.22, gain: 0.30 },
  ],
  // Single sharp whistle
  whistle: [
    { f: 1760, t: 0.0,  d: 0.10, gain: 0.32 },
    { f: 1980, t: 0.11, d: 0.16, gain: 0.30 },
  ],
  // Wet "bloop" — water-drop cue used by kitchen when an order carries
  // special notes / extras / allergies so it stands out from the classic
  // chime. Frequency sweeps down then back up like a submerged tap.
  blob: [
    { f: 620,  t: 0.00, d: 0.09, gain: 0.34 },
    { f: 330,  t: 0.08, d: 0.16, gain: 0.36 },
    { f: 880,  t: 0.28, d: 0.18, gain: 0.32 },
  ],
  silent: [],
};

export function playNotificationSound(kind, ctxRefLike) {
  const id = kind || getStoredSoundPref();
  if (!id || id === "silent") return;
  const pattern = PATTERNS[id] || PATTERNS.classic;
  if (!pattern.length) return;
  const ctx = ensureCtx(ctxRefLike);
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    pattern.forEach((tone) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = tone.f;
      gain.gain.setValueAtTime(0.0001, now + tone.t);
      gain.gain.exponentialRampToValueAtTime(tone.gain, now + tone.t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.t + tone.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + tone.t);
      osc.stop(now + tone.t + tone.d + 0.02);
    });
  } catch { /* ignore */ }
}

export function vibrateNotification(pattern) {
  const pref = getStoredSoundPref();
  if (pref === "silent") return;
  if (typeof navigator !== "undefined" && "vibrate" in navigator) {
    try { navigator.vibrate(pattern || [200, 100, 200]); } catch { /* ignore */ }
  }
}

export function getStoredSoundPref() {
  try { return localStorage.getItem(LOCAL_KEY) || "classic"; }
  catch { return "classic"; }
}

export function setStoredSoundPref(id) {
  try { localStorage.setItem(LOCAL_KEY, id); } catch { /* ignore */ }
}
